import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import SignInModal from "./SignInModal";
import { isAuthenticated, getUserProfile } from "../utils/auth";
import { isAdmin } from "../utils/auth";
import WishlistPopover from "./WishlistPopover";
import CartPopover from "./CartPopover";
import OrbieeLogo from "../images/Orbiee_logo_blue.png";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import orbieeAvatar from "../images/Orbiee_Avatra.png";

// Load all avatar icons dynamically using require.context
const avatarIconsContext = require.context("../Avatar_Icons", false, /_Icon\.png$/);
const avatarIcons = {};
avatarIconsContext.keys().forEach((item) => {
  const iconName = item.replace("./", "").replace("_Icon.png", "");
  avatarIcons[iconName] = avatarIconsContext(item);
});

export default function TopBar({
  activeMain,
  onWomenClick,
  onMenClick,
  onAccessoriesClick,
  onOnSaleClick,
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  onUserClick,
  isAuthModalOpen,
  onCloseAuthModal,
  totalItems,
  wishlistItems = 0,
  onOpenMenu,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const [wishlistAnchorEl, setWishlistAnchorEl] = useState(null);
  const [cartAnchorEl, setCartAnchorEl] = useState(null);
  const [profileHover, setProfileHover] = useState(false);
  const [userProfile, setUserProfile] = useState(getUserProfile());
  const [profileImageError, setProfileImageError] = useState(false);

  const handleWishlistClick = (e) => setWishlistAnchorEl(e.currentTarget);
  const handleWishlistClose = () => setWishlistAnchorEl(null);

  const handleCartClick = (e) => setCartAnchorEl(e.currentTarget);
  const handleCartClose = () => setCartAnchorEl(null);

  // Generate profile picture URL from user's full name
  const getProfilePictureUrl = () => {
    if (!userProfile?.customer_name) return null;
    
    // Convert customer_name to URL format (e.g., "Emily Parker" -> "emily.parker")
    const userfullname = userProfile.customer_name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '.');
    
    // Return the icon from the pre-loaded avatarIcons object
    return avatarIcons[userfullname] || null;
  };

  // Update user profile when auth state changes
  useEffect(() => {
    const updateProfile = () => {
      setUserProfile(getUserProfile());
      // Reset image error when profile changes
      setProfileImageError(false);
    };

    // Check immediately
    updateProfile();

    // Listen for auth state change events
    const handleAuthChange = () => {
      setTimeout(updateProfile, 200);
    };

    window.addEventListener("authStateChange", handleAuthChange);
    window.addEventListener("authStateChanged", handleAuthChange);

    return () => {
      window.removeEventListener("authStateChange", handleAuthChange);
      window.removeEventListener("authStateChanged", handleAuthChange);
    };
  }, []);

  const userName = userProfile?.customer_name || "";

  const handleBackClick = () => {
    navigate(-1);
  };
  const navLinks = [
    { label: "New Arrivals", path: "/new-arrivals" },
    {
      label: "Women",
      path: "#",
      onClick: onWomenClick,
      active: activeMain === "WOMEN",
    },
    {
      label: "Men",
      path: "#",
      onClick: onMenClick,
      active: activeMain === "MEN",
    },
    {
      label: "Accessories",
      path: "#",
      onClick: onAccessoriesClick,
      active: activeMain === "ACCESSORIES",
    },
   
    {
      label: "Sale",
      path: "#",
      onClick: onOnSaleClick,
      active: activeMain === "ONSALE",
      isSale: true,
    },
  ];

  return (
    <div className="topbar-container">
      <div className="topbar-content">
        {/* Back Button & Logo - Left */}
        <div className="topbar-logo-section flex items-center gap-3">
          {!isHomePage && (
            <button
              onClick={handleBackClick}
              className="text-gray-600 hover:text-gray-900 transition-colors p-1"
              aria-label="Go back"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}
          <Link to="/" className="topbar-logo-link">
            <img src={OrbieeLogo} alt="Orbiee" className="h-12 w-auto" />
          </Link>
        </div>

        {/* Navigation Links - Center Left */}
        <nav className="topbar-nav-links">
          {navLinks.map((link, index) => {
            if (link.disabled) {
              return (
                <span key={index} className="topbar-nav-link disabled">
                  {link.label}
                </span>
              );
            }
            if (link.onClick) {
              return (
                <button
                  key={index}
                  type="button"
                  onClick={link.onClick}
                  className={`topbar-nav-link ${link.active ? "active" : ""} ${link.isSale ? "sale" : ""}`}
                >
                  {link.label}
                </button>
              );
            }
            return (
              <Link
                key={index}
                to={link.path}
                className={`topbar-nav-link ${link.isSale ? "sale" : ""}`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* Search Bar - Center */}
        <div className="topbar-search-section">
          <form onSubmit={onSearchSubmit} className="topbar-search-form">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="topbar-search-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search products..."
              className="topbar-search-input"
            />
          </form>
        </div>

        {/* Icons - Right */}
        <div className="topbar-icons-section">
          {isAuthModalOpen && <SignInModal onClose={onCloseAuthModal} />}

          {/* Wishlist Icon with Badge */}
          <button
            type="button"
            className="topbar-icon-btn relative"
            onClick={handleWishlistClick}
            aria-label="Wishlist"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="topbar-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            {wishlistItems > 0 && (
              <span className="topbar-wishlist-badge">{wishlistItems}</span>
            )}
          </button>

          {/* Cart Icon with Badge */}
          <button
            type="button"
            className="topbar-icon-btn relative"
            onClick={handleCartClick}
            aria-label="Shopping cart"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="topbar-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            {totalItems > 0 && (
              <span className="topbar-cart-badge">{totalItems}</span>
            )}
          </button>

          {/* Profile Icon / Avatar */}
          <div
            style={{ position: "relative" }}
            onMouseEnter={() => setProfileHover(true)}
            onMouseLeave={() => setProfileHover(false)}
          >
            <button
              type="button"
              onClick={onUserClick}
              className="topbar-icon-btn"
              aria-label={
                !isAuthenticated()
                  ? "Sign In"
                  : isAdmin()
                  ? "Admin Panel"
                  : "Account"
              }
              style={{
                padding: 0,
                borderRadius: "50%",
                overflow: "hidden",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              {isAuthenticated() && userProfile ? (
                <img
                  src={profileImageError ? orbieeAvatar : (getProfilePictureUrl() || orbieeAvatar)}
                  alt="Profile"
                  onError={() => setProfileImageError(true)}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <AccountCircleIcon className="topbar-icon" />
              )}
            </button>
            {/* Username Tooltip on Hover */}
            {isAuthenticated() && userProfile && profileHover && userName && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: "8px",
                  padding: "6px 12px",
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  color: "white",
                  borderRadius: "4px",
                  fontSize: "14px",
                  whiteSpace: "nowrap",
                  zIndex: 1000,
                  pointerEvents: "none",
                }}
              >
                {userName}
                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    right: "12px",
                    width: 0,
                    height: 0,
                    borderLeft: "5px solid transparent",
                    borderRight: "5px solid transparent",
                    borderBottom: "5px solid rgba(0, 0, 0, 0.8)",
                  }}
                />
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="topbar-mobile-menu-btn"
            onClick={onOpenMenu}
            aria-label="Open menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="topbar-mobile-menu-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>
      {/* Popovers */}
      <WishlistPopover
        anchorEl={wishlistAnchorEl}
        onClose={handleWishlistClose}
      />
      <CartPopover anchorEl={cartAnchorEl} onClose={handleCartClose} />
    </div>
  );
}
